import useSWRV from 'swrv';
import http, { HTTPResultAsync } from '@/utils/http';
import useSwrvState from '@/utils/useSwrvState';
import { generalAppVariables } from '@/constants';
import { SignUpInvitedRequest } from '../user/dto/SignUpInvited';
import { CreateCompanyRequest } from './dto/CreateCompany';
import { GetCompanyRequest } from './dto/GetCompany';
import { UpdateCompanyRequest } from './dto/UpdateCompany';
import { InviteMemberRequest } from './dto/InviteMember';
import { GetUsersRequest } from './dto/GetUsers';
import ICompany from './ICompany';
import ICompanyUser from './ICompanyUser';
import { RemoveCompanyDataRequest } from './dto/RemoveCompanyData';

export async function removeCompanyData(payload: RemoveCompanyDataRequest): HTTPResultAsync {
  return http.sendSafe('DELETE', `/companies/${payload.company}`, { verificationCode: payload.verificationCode });
}

export async function get(payload: GetCompanyRequest): Promise<ICompany[]> {
  const resp = await http.send('GET', `/companies/${payload._id}`);
  return resp.payload as ICompany[];
}

export async function getUsers(payload: GetUsersRequest) : Promise<ICompanyUser[]> {
  const resp = await http.send('GET', `/companies/${payload._id}/users`);
  return resp.payload as ICompanyUser[];
}

// Swr companion of get
function getSwr(payload: GetCompanyRequest) {
  const {
    data,
    error,
    mutate,
    isValidating,
  } = useSWRV(
    () => payload && `/companies/${payload._id}`,
    () => get(payload),
    {
      revalidateOnFocus: false,
      refreshInterval: generalAppVariables.REFRESH_INTERVAL,
    },
  );

  const swrState = useSwrvState(data, error, isValidating);
  return {
    data,
    error,
    mutate,
    isValidating,
    swrState,
  };
}

function getUsersSwr(payload: GetUsersRequest) {
  const {
    data,
    error,
    mutate,
    isValidating,
  } = useSWRV(
    () => payload && `/companies/${payload._id}/users`,
    () => getUsers(payload),
    {
      revalidateOnFocus: false,
      refreshInterval: generalAppVariables.REFRESH_INTERVAL,
    },
  );

  const swrState = useSwrvState(data, error, isValidating);
  return {
    data,
    error,
    mutate,
    isValidating,
    swrState,
  };
}

export const swr = {
  get: getSwr,
  getUsers: getUsersSwr,
};

export async function create(payload: CreateCompanyRequest, idempotencyKey: string): HTTPResultAsync {
  const { designation, onboarding, ...rest } = payload;
  if (onboarding) {
    return http.sendSafe('POST', `/companies?onboarding=${onboarding}&designation=${designation}`, rest, { 'idempotency-key': idempotencyKey });
  }
  return http.sendSafe('POST', '/companies', rest, { 'idempotency-key': idempotencyKey });
}

export async function update(_id: string, payload: UpdateCompanyRequest): HTTPResultAsync {
  if (payload.uploadLogo) {
    const { uploadLogo, removeLogo: _removeLogo, ...payloadExcluded } = payload;
    return http.sendSafe('PUT', `/companies/${_id}`, payloadExcluded, undefined, { logo: uploadLogo });
  }
  return http.sendSafe('PUT', `/companies/${_id}`, payload);
}

export async function invite(company: string, payload: InviteMemberRequest[]): HTTPResultAsync {
  return http.sendSafe('POST', `/companies/${company}/invitations`, { invitations: payload });
}

export async function deleteMember(company: string, user: string): HTTPResultAsync {
  return http.sendSafe('DELETE', `/companies/${company}/users/${user}`);
}

export async function deleteInvitation(company: string, invitation: string): HTTPResultAsync {
  return http.sendSafe('DELETE', `/companies/${company}/invitations/${invitation}`);
}

export async function verifyInviteToken(token: string): HTTPResultAsync {
  return http.sendSafe('POST', '/companies/users/check-invited', { token });
}

export async function signUpInvited(payload: SignUpInvitedRequest): HTTPResultAsync {
  return http.sendSafe('POST', '/companies/users/invited', payload);
}
